import axios from 'axios';

// Base API URL - MUST be configured via environment variable in production
// M7 fix: Removed hardcoded localhost fallback for security
const API_URL = process.env.REACT_APP_API_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5000/api'
    : `${window.location.protocol}//api.${window.location.hostname.replace('app.', '')}/api`
);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for CSRF
});

// CSRF token management
let csrfToken = null;

// Fetch CSRF token on app load
export const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/csrf-token`, { withCredentials: true });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    // Silent fail - CSRF token will be retried on next request
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to fetch CSRF token:', error);
    }
  }
};

// Add request interceptor to include auth token and CSRF token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for mutating requests
    if (csrfToken && ['post', 'put', 'delete', 'patch'].includes(config.method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
      console.error('Access forbidden:', error.response.data?.error || 'You do not have permission to perform this action');
    } else if (error.response?.status === 404) {
      // Not found
      console.error('Resource not found:', error.response.data?.error || 'The requested resource was not found');
    } else if (error.response?.status >= 500) {
      // Server error
      console.error('Server error:', error.response.data?.error || 'An unexpected server error occurred');
    } else if (!error.response) {
      // Network error
      console.error('Network error: Unable to connect to the server');
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints
export const authAPI = {
  register: (userData) => api.post('/auth/signup', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  requestPasswordReset: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: () => api.post('/auth/resend-verification'),
  // M5 fix: Added founding member check
  checkFoundingMember: (stateCode) => api.get(`/auth/founding-member-check/${stateCode.toUpperCase()}`),
};

// User endpoints
export const userAPI = {
  getMe: () => api.get('/auth/me'),
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  exportData: () => api.get('/users/export-data'),
  deleteAccount: (password) => api.delete('/users/account', { data: { password, confirmation: 'DELETE' } }),
  updatePassword: (data) => api.put('/users/password', data),
  updateEmail: (data) => api.put('/users/email', data),
  requestVerification: (data) => api.post('/users/request-verification', data),
  getVerificationStatus: () => api.get('/users/verification-status'),
  cancelVerification: () => api.post('/users/cancel-verification'),
  getBadges: () => api.get('/users/badges'),
  getHomeownerProfile: (homeownerId) => api.get(`/users/homeowner/${homeownerId}`),
};

// Contractor endpoints
export const contractorAPI = {
  browseContractors: (params) => api.get('/contractors', { params }),
  getProfile: (contractorId) => api.get(`/contractors/${contractorId}`),
  updateProfile: (data) => api.put('/contractors/profile', data),
  updateTrades: (categoryIds) => api.put('/contractors/trades', { category_ids: categoryIds }),

  // Portfolio management
  getMyPortfolio: () => api.get('/contractors/portfolio/my'),
  updatePortfolioItem: (photoId, data) => api.put(`/contractors/portfolio/${photoId}`, data),
  reorderPortfolio: (photoOrders) => api.put('/contractors/portfolio/reorder', { photo_orders: photoOrders }),
  getCompletedJobs: () => api.get('/contractors/completed-jobs'),

  // Favorites management
  getFavorites: () => api.get('/favorites'),
  addFavorite: (contractorId) => api.post(`/favorites/${contractorId}`),
  removeFavorite: (contractorId) => api.delete(`/favorites/${contractorId}`),
};

// Job endpoints
export const jobAPI = {
  getAllJobs: (params) => api.get('/jobs', { params }),
  getJobById: (id) => api.get(`/jobs/${id}`),
  createJob: (jobData) => api.post('/jobs', jobData),
  updateJob: (id, jobData) => api.put(`/jobs/${id}`, jobData),
  deleteJob: (id) => api.delete(`/jobs/${id}`),
  getMyJobs: () => api.get('/jobs/my-jobs'),
  getUserJobs: (userId) => api.get(`/jobs/user/${userId}`),
  createSubJob: (agreementId, jobData) => api.post(`/jobs/${agreementId}/create-subjob`, jobData),
  getSubcontractors: (jobId) => api.get(`/jobs/${jobId}/subcontractors`),
  getCategories: () => api.get('/jobs/categories/all'),

  // Job Interests (Lead Marketplace Model)
  expressInterest: (jobId, data) => api.post(`/jobs/${jobId}/interest`, data),
  getInterestedContractors: (jobId) => api.get(`/jobs/${jobId}/interested-contractors`),
  unlockContractor: (jobId, contractorId) => api.post(`/jobs/${jobId}/unlock-contractor/${contractorId}`),
  getMyInterest: (jobId) => api.get(`/jobs/${jobId}/my-interest`),
};

// Job Alert endpoints
export const jobAlertAPI = {
  getAlerts: () => api.get('/job-alerts'),
  createAlert: (alertData) => api.post('/job-alerts', alertData),
  updateAlert: (alertId, alertData) => api.put(`/job-alerts/${alertId}`, alertData),
  toggleAlert: (alertId) => api.patch(`/job-alerts/${alertId}/toggle`),
  deleteAlert: (alertId) => api.delete(`/job-alerts/${alertId}`),
};

// Bid endpoints
export const bidAPI = {
  createBid: (bidData) => api.post('/bids', bidData),
  getBidById: (id) => api.get(`/bids/${id}`),
  updateBid: (id, bidData) => api.put(`/bids/${id}`, bidData),
  deleteBid: (id) => api.delete(`/bids/${id}`),
  getJobBids: (jobId) => api.get(`/bids/job/${jobId}`),
  getMyBids: () => api.get('/bids/my/submitted'),
  acceptBid: (id) => api.post(`/bids/${id}/accept`),
  declineBid: (id) => api.post(`/bids/${id}/decline`),
  counterOffer: (id, data) => api.post(`/bids/${id}/counter-offer`, data),
  acceptCounterOffer: (id, data) => api.post(`/bids/${id}/accept-counter-offer`, data),
  declineCounterOffer: (id) => api.post(`/bids/${id}/decline-counter-offer`),
  getReceivedBids: () => api.get('/bids/received'),
};

// Message endpoints
export const messageAPI = {
  getConversations: (params) => api.get('/messages/conversations', { params }),
  getThread: (jobId, otherUserId) => api.get(`/messages/thread/${jobId}/${otherUserId}`),
  sendMessage: (data) => api.post('/messages', data),
  markAsRead: (jobId, otherUserId) => api.put(`/messages/mark-read/${jobId}/${otherUserId}`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  deleteMessage: (messageId) => api.delete(`/messages/${messageId}`),
  // Conversation management
  archiveConversation: (jobId, otherUserId) => api.put(`/messages/conversation/archive/${jobId}/${otherUserId}`),
  unarchiveConversation: (jobId, otherUserId) => api.put(`/messages/conversation/unarchive/${jobId}/${otherUserId}`),
  deleteConversation: (jobId, otherUserId) => api.delete(`/messages/conversation/${jobId}/${otherUserId}`),
  // Attachments
  uploadAttachment: (messageId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/messages/${messageId}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (attachmentId) => api.delete(`/messages/attachment/${attachmentId}`),
};

// Notification endpoints
export const notificationAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread/count'),
  markAsRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
  deleteAllRead: () => api.delete('/notifications/read/all'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (preferences) => api.put('/notifications/preferences', preferences),
};

// Review endpoints
export const reviewAPI = {
  createReview: (data) => api.post('/reviews', data),
  getContractorReviews: (contractorId, params) => api.get(`/reviews/contractor/${contractorId}`, { params }),
  getContractorSummary: (contractorId) => api.get(`/reviews/contractor/${contractorId}/summary`),
  getMyGivenReviews: () => api.get('/reviews/my/given'),
  getMyReceivedReviews: () => api.get('/reviews/my/received'),
  respondToReview: (reviewId, response) => api.post(`/reviews/${reviewId}/response`, { response }),
  markJobComplete: (agreementId) => api.post(`/reviews/complete/${agreementId}`),
  canReview: (agreementId) => api.get(`/reviews/can-review/${agreementId}`),
  flagReview: (reviewId, reason) => api.post(`/reviews/${reviewId}/flag`, { reason }),
  // Contractor reviews for homeowners
  submitHomeownerReview: (agreementId, rating, reviewText) => api.post('/reviews/homeowner', { agreement_id: agreementId, rating, review_text: reviewText }),
  getHomeownerReviews: (homeownerId, page = 1, limit = 10) => api.get(`/reviews/homeowner/${homeownerId}`, { params: { page, limit } }),
  getHomeownerRatingSummary: (homeownerId) => api.get(`/reviews/homeowner/${homeownerId}/summary`),
};

// Subscription endpoints
export const subscriptionAPI = {
  createCheckoutSession: (plan = 'monthly', promoCode = '') => api.post('/subscription/create-checkout-session', { plan, promo_code: promoCode }),
  validatePromo: (promoCode) => api.post('/subscription/validate-promo', { promo_code: promoCode }),
  getStatus: () => api.get('/subscription/status', {
    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' },
    params: { _t: Date.now() } // Cache busting
  }),
  cancel: () => api.post('/subscription/cancel'),
};

// Upload endpoints
export const uploadAPI = {
  uploadJobPhotos: (jobId, formData) => {
    return api.post(`/upload/job-photo/${jobId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfilePhoto: (formData) => {
    return api.post('/upload/profile-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPortfolioPhotos: (formData) => {
    return api.post('/upload/portfolio-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadLicenseDocument: (formData) => {
    return api.post('/upload/license-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadInsuranceDocument: (formData) => {
    return api.post('/upload/insurance-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteJobPhoto: (photoId) => api.delete(`/upload/job-photo/${photoId}`),
  deletePortfolioPhoto: (photoId) => api.delete(`/upload/portfolio-photo/${photoId}`),
  deleteLicenseDocument: () => api.delete('/upload/license-document'),
  deleteInsuranceDocument: () => api.delete('/upload/insurance-document'),
};

// Invoice endpoints
export const invoiceAPI = {
  getAllInvoices: () => api.get('/invoices'),
  getInvoiceById: (id) => api.get(`/invoices/${id}`),
  getInvoicesByJob: (jobId) => api.get(`/invoices/by-job/${jobId}`),
  createInvoice: (data) => api.post('/invoices', data),
  updateInvoice: (id, data) => api.put(`/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/invoices/${id}`),
  getChecklist: (trade) => api.get(`/invoices/checklist/${trade}`),
  sendInvoice: (id, options = {}) => api.post(`/invoices/${id}/send`, options),
  updateInvoiceStatus: (id, status) => api.patch(`/invoices/${id}/status`, { status }),
};

// Agreement endpoints
export const agreementAPI = {
  getMyAgreements: () => api.get('/agreements/my-agreements'),
  getAgreementById: (agreementId) => api.get(`/agreements/${agreementId}`),
  requestCancellation: (agreementId, reason) => api.post(`/agreements/${agreementId}/request-cancellation`, { reason }),
  respondToCancellation: (agreementId, approve, notes) => api.post(`/agreements/${agreementId}/respond-cancellation`, { approve, notes }),
  completeAssessment: (agreementId, data) => api.post(`/agreements/${agreementId}/complete-assessment`, data),
  approveFinalQuote: (agreementId) => api.post(`/agreements/${agreementId}/approve-final-quote`),
};

// Dispute endpoints
export const disputeAPI = {
  fileDispute: (data) => api.post('/disputes/file', data),
  getMyDisputes: () => api.get('/disputes/my-disputes'),
  getDisputeById: (disputeId) => api.get(`/disputes/${disputeId}`),
  addMessage: (disputeId, messageText) => api.post(`/disputes/${disputeId}/message`, { message_text: messageText }),

  // Admin endpoints (monitoring only - no resolution/decisions)
  getAllDisputes: (params) => api.get('/disputes/admin/all', { params }),
  closeDispute: (disputeId) => api.post(`/disputes/${disputeId}/close`), // Archive only, does not resolve
};

// Admin endpoints
export const adminAPI = {
  // Dashboard
  getDashboardStats: () => api.get('/admin/stats'),

  // User management
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  suspendUser: (userId, reason) => api.post(`/admin/users/${userId}/suspend`, { reason }),
  reactivateUser: (userId, reason) => api.post(`/admin/users/${userId}/reactivate`, { reason }),
  deleteUser: (userId, reason) => api.delete(`/admin/users/${userId}`, { data: { reason: reason || 'Admin deletion' } }),
  bulkDeleteUsers: (userIds) => api.post('/admin/users/bulk-delete', { userIds }),
  verifyUserEmail: (userId) => api.post(`/admin/users/${userId}/verify-email`),
  upgradeSubscription: (userId, data) => api.post(`/admin/users/${userId}/upgrade-subscription`, data),
  resetTrial: (userId, reason) => api.post(`/admin/users/${userId}/reset-trial`, { reason }),
  grantLifetime: (userId, reason) => api.post(`/admin/users/${userId}/grant-lifetime`, { reason }),
  grantSubscription: (userId, data) => api.post(`/admin/users/${userId}/grant-subscription`, data),
  grantFounder: (userId, isFounder, reason) => api.post(`/admin/users/${userId}/grant-founder`, { is_founder: isFounder, reason }),
  makeAdmin: (userId, data) => api.post(`/admin/users/${userId}/make-admin`, data),
  removeAdmin: (userId, reason) => api.post(`/admin/users/${userId}/remove-admin`, { reason }),

  // Job moderation
  getAllJobs: (params) => api.get('/admin/jobs', { params }),
  getPendingJobs: (params) => api.get('/admin/jobs/pending', { params }),
  getJobBids: (jobId, status) => api.get(`/admin/jobs/${jobId}/bids`, { params: { status } }),
  approveJob: (jobId) => api.post(`/admin/jobs/${jobId}/approve`),
  rejectJob: (jobId, reason) => api.post(`/admin/jobs/${jobId}/reject`, { reason }),
  deleteJob: (jobId, reason) => api.delete(`/admin/jobs/${jobId}`, { data: { reason } }),
  fastCompleteJob: (jobId) => api.post(`/admin/jobs/${jobId}/fast-complete`),

  // Subscription management
  updateUserSubscription: (userId, data) => api.put(`/admin/subscriptions/${userId}`, data),

  // Promo codes
  getPromoCodes: () => api.get('/admin/promo-codes'),
  generatePromoCode: (data) => api.post('/admin/promo-codes/generate', data),
  deactivatePromoCode: (codeId) => api.put(`/admin/promo-codes/${codeId}/deactivate`),
  deletePromoCode: (codeId) => api.delete(`/admin/promo-codes/${codeId}`),

  // Support Tickets
  getSupportTickets: (params) => api.get('/admin/support-tickets', { params }),
  getSupportTicket: (ticketId) => api.get(`/admin/support-tickets/${ticketId}`),
  updateSupportTicketStatus: (ticketId, data) => api.put(`/admin/support-tickets/${ticketId}/status`, data),
  deleteSupportTicket: (ticketId) => api.delete(`/admin/support-tickets/${ticketId}`),

  // Refunds
  processRefund: (subscriptionId, data) => api.post(`/admin/refunds/${subscriptionId}`, data),

  // Contractor verification
  getPendingContractors: () => api.get('/admin/contractors/pending-verification'),
  approveContractor: (userId, adminNotes) => api.post(`/admin/contractors/${userId}/approve`, { admin_notes: adminNotes }),
  rejectContractor: (userId, rejectionReason) => api.post(`/admin/contractors/${userId}/reject`, { rejection_reason: rejectionReason }),

  // Account verification
  getPendingVerifications: () => api.get('/admin/verifications/pending'),
  approveVerification: (userId, notes) => api.post(`/admin/users/${userId}/verify`, { notes }),
  rejectVerification: (userId, reason) => api.post(`/admin/users/${userId}/reject-verification`, { reason }),

  // Contractor license/insurance verification
  verifyLicense: (contractorId, verified, notes) => api.post(`/admin/contractors/${contractorId}/verify-license`, { verified, notes }),
  verifyInsurance: (contractorId, verified, notes) => api.post(`/admin/contractors/${contractorId}/verify-insurance`, { verified, notes }),

  // Analytics
  getRevenueAnalytics: (params) => api.get('/admin/analytics/revenue', { params }),
  getEngagementMetrics: () => api.get('/admin/analytics/engagement'),
  getPlatformHealth: () => api.get('/admin/analytics/health'),

  // Data export
  exportUsersCSV: (params) => api.get('/admin/export/users', { params, responseType: 'blob' }),
  exportJobsCSV: (params) => api.get('/admin/export/jobs', { params, responseType: 'blob' }),

  // Admin actions log
  getAdminActions: (params) => api.get('/admin/actions', { params }),

  // Founding Members management
  getFoundingMembers: () => api.get('/admin/founding-members'),
  getFoundingMembersByState: (stateCode) => api.get(`/admin/founding-members/${stateCode}`),
  updateFoundingMemberState: (stateCode, data) => api.put(`/admin/founding-members/${stateCode}`, data),
  resetFoundingMemberState: (stateCode) => api.post(`/admin/founding-members/${stateCode}/reset`),

  // Newsletter management
  getNewsletterAudienceCounts: () => api.get('/newsletter/admin/audience-counts'),
  sendNewsletter: (data) => api.post('/newsletter/admin/send', data),
  sendTestNewsletter: (data) => api.post('/newsletter/admin/test', data),
  getScheduledEmails: () => api.get('/newsletter/admin/scheduled'),
  cancelScheduledEmail: (id) => api.delete(`/newsletter/admin/scheduled/${id}`),
  getEmailHistory: (params) => api.get('/newsletter/admin/history', { params }),

  // Manual/Concierge Bids (for concierge marketplace phase)
  createManualBid: (data) => api.post('/admin/bids/manual', data),
  getManualBids: (params) => api.get('/admin/bids/manual', { params }),
  updateManualBid: (bidId, data) => api.put(`/admin/bids/manual/${bidId}`, data),
  deleteManualBid: (bidId) => api.delete(`/admin/bids/manual/${bidId}`),

  // Concierge Phase - Post jobs on behalf of homeowners
  postJobOnBehalf: (data) => api.post('/admin/post-job-behalf', data),

  // Live statistics dashboard
  getLiveStats: () => api.get('/admin/live-stats'),

  // Contractor Leads CRM (private admin notepad)
  getContractorLeads: (params) => api.get('/admin/contractor-leads', { params }),
  addContractorLead: (data) => api.post('/admin/contractor-leads', data),
  updateContractorLead: (id, data) => api.put(`/admin/contractor-leads/${id}`, data),
  deleteContractorLead: (id) => api.delete(`/admin/contractor-leads/${id}`),
  sendContractorInvite: (id, data) => api.post(`/admin/contractor-leads/${id}/invite`, data),
  getAvailableJobsForInvite: (params) => api.get('/admin/contractor-leads/available-jobs', { params }),
};

// Admin Lead Review (homeowner-side leads from landing pages)
// Manual-publish only — auto_publish_leads platform flag is intentionally not exposed.
export const adminLeadsAPI = {
  getStats: () => api.get('/admin/leads/stats'),
  list: (params) => api.get('/admin/leads', { params }),
  create: (data) => api.post('/admin/leads', data),
  get: (id) => api.get(`/admin/leads/${id}`),
  update: (id, data) => api.patch(`/admin/leads/${id}`, data),
  rescore: (id) => api.post(`/admin/leads/${id}/rescore`),
  markSpam: (id) => api.post(`/admin/leads/${id}/spam`),
  unmarkSpam: (id) => api.post(`/admin/leads/${id}/unspam`),
  archive: (id) => api.post(`/admin/leads/${id}/archive`),
  unarchive: (id) => api.post(`/admin/leads/${id}/unarchive`),
  delete: (id, data) => api.delete(`/admin/leads/${id}`, { data }),
  publish: (id, data) => api.post(`/admin/leads/${id}/publish`, data),
  eligibleContractors: (id, params) => api.get(`/admin/leads/${id}/eligible-contractors`, { params }),
  assignContractor: (id, data) => api.post(`/admin/leads/${id}/assign-contractor`, data),
  getTradeCategories: () => api.get('/admin/leads/_meta/trade-categories'),
};

// Organization endpoints (for business accounts)
export const organizationAPI = {
  getMyOrganizations: () => api.get('/organizations/my-organizations'),
  getOrganization: (orgId) => api.get(`/organizations/${orgId}`),
  updateOrganization: (orgId, data) => api.put(`/organizations/${orgId}`, data),
  getProperties: (orgId) => api.get(`/organizations/${orgId}/properties`),
  getMembers: (orgId) => api.get(`/organizations/${orgId}/members`),

  // HOA management methods
  getCoverageAreas: (orgId) => api.get(`/organizations/${orgId}/hoa/coverage-areas`),
  addCoverageArea: (orgId, data) => api.post(`/organizations/${orgId}/hoa/coverage-areas`, data),
  getCommunityJobs: (orgId) => api.get(`/organizations/${orgId}/hoa/community-jobs`),
  getActiveContractors: (orgId) => api.get(`/organizations/${orgId}/hoa/active-contractors`),
  getHOAStatistics: (orgId) => api.get(`/organizations/${orgId}/hoa/statistics`),
  getMemberRequests: (orgId) => api.get(`/organizations/${orgId}/hoa/member-requests`),
  getHOAMembers: (orgId) => api.get(`/organizations/${orgId}/hoa/members`),
  approveMemberRequest: (orgId, requestId, data) => api.post(`/organizations/${orgId}/hoa/member-requests/${requestId}/approve`, data),
  approveContractor: (orgId, contractorId) => api.post(`/organizations/${orgId}/hoa/contractors/${contractorId}/approve`),
  flagJob: (orgId, jobId, data) => api.post(`/organizations/${orgId}/hoa/jobs/${jobId}/flag`, data),
};

// HOA endpoints (for HOA community features)
export const hoaAPI = {
  // Announcements
  getAnnouncements: () => api.get('/hoa/announcements'),
  createAnnouncement: (data) => api.post('/hoa/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/hoa/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/hoa/announcements/${id}`),

  // Messages
  getMessages: (parentId) => api.get('/hoa/messages', { params: parentId ? { parent_id: parentId } : {} }),
  postMessage: (data) => api.post('/hoa/messages', data),
  likeMessage: (id) => api.post(`/hoa/messages/${id}/like`),
  pinMessage: (id) => api.put(`/hoa/messages/${id}/pin`),
  deleteMessage: (id) => api.delete(`/hoa/messages/${id}`),

  // Polls
  getPolls: () => api.get('/hoa/polls'),
  getPoll: (id) => api.get(`/hoa/polls/${id}`),
  createPoll: (data) => api.post('/hoa/polls', data),
  vote: (pollId, data) => api.post(`/hoa/polls/${pollId}/vote`, data),
  closePoll: (id) => api.put(`/hoa/polls/${id}/close`),

  // Maintenance
  getMaintenanceRequests: () => api.get('/hoa/maintenance'),
  createMaintenanceRequest: (data) => api.post('/hoa/maintenance', data),
  updateMaintenanceRequest: (id, data) => api.put(`/hoa/maintenance/${id}`, data),

  // Documents
  getDocuments: () => api.get('/hoa/documents'),
  uploadDocument: (data) => api.post('/hoa/documents', data),
  deleteDocument: (id) => api.delete(`/hoa/documents/${id}`),

  // Payments
  getMyPayments: () => api.get('/hoa/payments'),
  getAllPayments: () => api.get('/hoa/payments/all'),
  createPayment: (data) => api.post('/hoa/payments', data),
  markPaid: (id) => api.put(`/hoa/payments/${id}/mark-paid`),

  // Residents
  getResidents: () => api.get('/hoa/residents'),
  updateVisibility: (data) => api.put('/hoa/residents/visibility', data),

  // Search & Join
  searchHOA: (query) => api.get('/organizations/hoa/search', { params: { query } }),
  getMyAssociation: () => api.get('/organizations/my-hoa-association'),
  joinRequest: (orgId, data) => api.post(`/organizations/${orgId}/hoa/join-request`, data),
  leaveHOA: (orgId) => api.delete(`/organizations/${orgId}/hoa/leave`),
};

// Property endpoints (for homeowners)
export const propertyAPI = {
  getMyProperties: () => api.get('/properties'),
  createProperty: (propertyData) => api.post('/properties', propertyData),
  updateProperty: (propertyId, propertyData) => api.put(`/properties/${propertyId}`, propertyData),
  deleteProperty: (propertyId) => api.delete(`/properties/${propertyId}`),
};

// Referral endpoints
export const referralAPI = {
  getMyReferralStats: () => api.get('/referrals/my-stats'),
  getMonthlyReferralHistory: () => api.get('/referrals/monthly-history'),
};

// Refund endpoints
export const refundAPI = {
  requestRefund: (data) => api.post('/refunds/request', data),
  getMyRefundRequests: () => api.get('/refunds/my-requests'),
  getAllRefundRequests: (status) => api.get('/refunds/admin/requests', { params: { status } }),
  processRefundRequest: (requestId, action, admin_notes) => api.post(`/refunds/admin/process/${requestId}`, { action, admin_notes }),
};

// Support endpoints
export const supportAPI = {
  submitTicket: (data) => api.post('/support/contact', data),
  getMyTickets: () => api.get('/support/my-tickets'),
};

// Giveaway & Referral endpoints
export const giveawayAPI = {
  // Public endpoints
  getActiveGiveaway: () => api.get('/giveaways/active'),
  getLeaderboard: (limit = 10) => api.get(`/referrals/leaderboard?limit=${limit}`),
  validateReferralCode: (code) => api.get(`/referrals/validate/${code}`),

  // User endpoints (auth required)
  getMyReferralCode: () => api.get('/referrals/my-code'),
  getMyReferralStats: () => api.get('/referrals/stats'),
  getMyGiveawayEntry: () => api.get('/giveaways/my-entry'),

  // Admin endpoints
  createGiveaway: (data) => api.post('/giveaways', data),
  getAllGiveaways: () => api.get('/giveaways'),
  getGiveaway: (id) => api.get(`/giveaways/${id}`),
  updateGiveawayStatus: (id, status) => api.put(`/giveaways/${id}/status`, { status }),
  getGiveawayEntries: (id) => api.get(`/giveaways/${id}/entries`),
  drawWinner: (id) => api.post(`/giveaways/${id}/draw`),
  redrawWinner: (id) => api.post(`/giveaways/${id}/redraw`),
  addMailInEntry: (id, data) => api.post(`/giveaways/${id}/mail-in-entry`, data),
  addTicketsManually: (id, data) => api.post(`/giveaways/${id}/add-tickets`, data),

  // Webhook
  processSignup: (data) => api.post('/giveaway-webhooks/user-signup', data),
};

// Platform endpoints (beta mode, settings)
export const platformAPI = {
  // Public endpoint
  getBetaStatus: () => api.get('/platform/beta-status'),

  // Admin endpoints
  toggleBetaMode: (enabled) => api.post('/platform/beta-mode/toggle', { enabled }),
  getBetaStats: () => api.get('/platform/beta-stats'),
};

// Public endpoints (no auth required)
export const publicAPI = {
  // Stats and metrics
  getStats: () => api.get('/stats'),
  getPublicStats: () => api.get('/jobs/public/stats'),

  // Public job browsing
  browseJobs: (params) => api.get('/jobs/public/browse', { params }),
  getActivity: (params) => api.get('/jobs/public/activity', { params }),

  // Trade categories
  getTradeCategories: () => api.get('/trade-categories'),

  // Testimonials
  getTestimonials: (params) => api.get('/reviews/public/testimonials', { params }),

  // Showcase/transformations
  getFeaturedTransformations: (params) => api.get('/showcase/featured-transformations', { params }),
};

// Google Business Profile endpoints
export const googleAPI = {
  // Get connection status (for contractors)
  getConnectionStatus: () => api.get('/google/status'),

  // Initiate OAuth connection - redirects to Google
  connect: () => {
    const token = localStorage.getItem('token');
    window.location.href = `${API_URL}/google/connect?token=${token}`;
  },

  // Disconnect Google Business
  disconnect: () => api.post('/google/disconnect'),

  // Manual sync trigger
  syncNow: () => api.post('/google/sync'),

  // Get Google reviews for a contractor (public)
  getReviews: (contractorId, params) => api.get(`/google/reviews/${contractorId}`, { params }),
};

// Blog API endpoints
export const blogAPI = {
  // Public endpoints
  getPosts: (params) => api.get('/blog', { params }),
  getPost: (slug) => api.get(`/blog/${slug}`),
  getCategories: () => api.get('/blog/categories'),

  // Admin endpoints
  adminGetPosts: (params) => api.get('/blog/admin/all', { params }),
  adminGetPost: (id) => api.get(`/blog/admin/${id}`),
  adminCreatePost: (data) => api.post('/blog/admin', data),
  adminUpdatePost: (id, data) => api.put(`/blog/admin/${id}`, data),
  adminDeletePost: (id) => api.delete(`/blog/admin/${id}`),
};

// Partner Promos API endpoints
export const partnerPromosAPI = {
  // Public endpoint
  validateCode: (code) => api.get(`/partner-promos/validate/${code}`),

  // Admin endpoints
  getAll: () => api.get('/partner-promos/admin/all'),
  getSignups: (promoId, params) => api.get(`/partner-promos/admin/${promoId}/signups`, { params }),
  getRecentSignups: (limit = 20) => api.get('/partner-promos/admin/recent-signups', { params: { limit } }),
  create: (data) => api.post('/partner-promos/admin', data),
  update: (id, data) => api.put(`/partner-promos/admin/${id}`, data),
  delete: (id) => api.delete(`/partner-promos/admin/${id}`),
  toggle: (id) => api.post(`/partner-promos/admin/${id}/toggle`),
};

// Instant Booking endpoints
export const instantBookingAPI = {
  createBooking: (data) => api.post('/instant-bookings', data),
  getMyBookings: (params) => api.get('/instant-bookings', { params }),
  getBookingById: (id) => api.get(`/instant-bookings/${id}`),
  confirmBooking: (id, notes) => api.put(`/instant-bookings/${id}/confirm`, { contractor_notes: notes || '' }),
  startBooking: (id) => api.put(`/instant-bookings/${id}/start`),
  completeBooking: (id, data) => api.put(`/instant-bookings/${id}/complete`, data),
  cancelBooking: (id, reason) => api.put(`/instant-bookings/${id}/cancel`, { cancellation_reason: reason }),
  getAvailableContractors: (params) => api.get('/instant-bookings/contractors/available', { params }),
};

export const availabilityAPI = {
  getMyStatus: () => api.get('/availability/my-status'),
  updateStatus: (data) => api.put('/availability/status', data),
  updateInstantBook: (data) => api.put('/availability/instant-book', data),
  getContractorAvailability: (contractorId) => api.get(`/availability/contractor/${contractorId}`),
  heartbeat: () => api.post('/availability/heartbeat'),
};

// Calendar endpoints
export const calendarAPI = {
  getEvents: (start, end) => api.get('/calendar/events', { params: { start, end } }),
};

// Maintenance reminder endpoints
export const maintenanceAPI = {
  getTemplates: (categoryId) => api.get('/maintenance/templates', { params: { category_id: categoryId } }),
  getReminders: (params) => api.get('/maintenance/reminders', { params }),
  createReminder: (data) => api.post('/maintenance/reminders', data),
  updateReminder: (id, data) => api.put(`/maintenance/reminders/${id}`, data),
  deleteReminder: (id) => api.delete(`/maintenance/reminders/${id}`),
  completeReminder: (id, data) => api.post(`/maintenance/reminders/${id}/complete`, data),
  snoozeReminder: (id, days) => api.post(`/maintenance/reminders/${id}/snooze`, { snooze_days: days }),
  createJobFromReminder: (id, data) => api.post(`/maintenance/reminders/${id}/create-job`, data),
  getDashboard: () => api.get('/maintenance/dashboard'),
  getSuggested: () => api.get('/maintenance/suggested'),
  createBulkReminders: (data) => api.post('/maintenance/reminders/bulk', data),
};

// Reports API (scam prevention)
export const reportsAPI = {
  getReasons: () => api.get('/reports/reasons'),
  reportJob: (jobId, data) => api.post(`/reports/job/${jobId}`, data),
  reportUser: (userId, data) => api.post(`/reports/user/${userId}`, data),
  // Admin endpoints
  getJobReports: (params) => api.get('/reports/admin/jobs', { params }),
  getUserReports: (params) => api.get('/reports/admin/users', { params }),
  updateJobReport: (reportId, data) => api.put(`/reports/admin/job/${reportId}`, data),
  getSuspiciousUsers: () => api.get('/reports/admin/suspicious'),
  getReportStats: () => api.get('/reports/admin/stats'),
};

export default api;
